<template>
  <div
    class="proposal-badge-checkbox-container"
    :class="`proposal-badge-source-${placeholderSource}`"
    :title="tooltipText"
    @click="handleAccept"
  >
    <!-- Visual representation of the proposed checkbox state -->
    <div class="proposal-badge-checkbox-visual">
      <!-- Source icon (AI sparkles, Latest receipt, etc.) -->
      <i :class="sourceIconClass" class="source-icon" />
      <!-- Checkbox state icon (check-circle or circle) -->
      <i :class="proposedIcon" />
      <!-- ON/OFF label -->
      <span class="proposal-value-label">{{ proposedValueDisplay }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { InvoiceItemSuggestionSource } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';

const i18n = useI18n();

interface Props {
  /** Source of the proposal (determines color scheme) */
  placeholderSource?: InvoiceItemSuggestionSource;
  /** The proposed value (boolean, guaranteed to be a single boolean) */
  placeholder: boolean | null;
  /** Current checkbox value */
  currentValue: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
}

interface Emits {
  (e: 'accept'): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholderSource: 'ai',
  disabled: false,
});

const emit = defineEmits<Emits>();

/**
 * Display text for the proposed value (ON/OFF, localized)
 */
const proposedValueDisplay = computed(() => {
  const key = props.placeholder ? 'buttons.on' : 'buttons.off';
  if (i18n.te(key)) {
    return i18n.t(key);
  }
  return props.placeholder ? 'ON' : 'OFF';
});

/**
 * Localized source label (AI, Latest, Other)
 */
const sourceLabel = computed(() => {
  const translationKeyBySource = {
    ai: 'labels.proposalSourceAi',
    latest: 'labels.proposalSourceLatest',
    other: 'labels.proposalSourceOther',
  } as const;

  const fallbackLabelBySource = {
    ai: 'AI',
    latest: 'Latest',
    other: 'Other',
  } as const;

  const source = props.placeholderSource ?? 'ai';
  const key = translationKeyBySource[source];

  if (i18n.te(key)) {
    return i18n.t(key);
  }

  return fallbackLabelBySource[source];
});

/**
 * Localized tooltip text for accessibility
 */
const tooltipText = computed(() => {
  // Example: "AI-Vorschlag: AN" or "Latest Proposal: ON"
  const translationKey = 'tooltips.proposalCheckbox';
  if (i18n.te(translationKey)) {
    return i18n.t(translationKey, {
      source: sourceLabel.value,
      value: proposedValueDisplay.value,
    });
  }

  return `${sourceLabel.value} Proposal: ${proposedValueDisplay.value}`;
});

/**
 * Icon for the source (AI, Latest, Other)
 */
const sourceIconClass = computed(() => {
  switch (props.placeholderSource) {
    case 'ai':
      return 'pi pi-sparkles'; // AI: sparkles icon
    case 'latest':
      return 'pi pi-receipt'; // Latest/Previous: receipt icon
    case 'other':
      return 'pi pi-circle-fill'; // Other: circle icon
    default:
      return 'pi pi-sparkles'; // Default to AI icon
  }
});

/**
 * Icon for the proposed value
 * TRUE/ON = checkmark circle, FALSE/OFF = empty circle
 */
const proposedIcon = computed(() => {
  return props.placeholder ? 'pi pi-check-circle' : 'pi pi-circle';
});

function handleAccept() {
  if (!props.disabled) {
    emit('accept');
  }
}
</script>

<style scoped>
/**
 * Visual proposal badge container for checkbox state
 * Shows the proposed ON/OFF state with icon and label
 * Darker background to clearly distinguish from regular badge
 */
.proposal-badge-checkbox-container {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.75rem;
  margin-left: 0.5rem;
  border-radius: 4px;
  border: 1.5px solid;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  font-weight: 500;
}

.proposal-badge-checkbox-visual {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.proposal-badge-checkbox-visual i {
  font-size: 0.95rem;
}

.proposal-badge-checkbox-visual .source-icon {
  font-size: 1rem;
  opacity: 0.9;
}

.proposal-value-label {
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

/* AI proposals: Purple palette */
.proposal-badge-source-ai {
  color: var(--placeholder-ai-color);
  background-color: var(--placeholder-ai-background);
  border-color: var(--placeholder-ai-color);
}

.proposal-badge-source-ai:hover {
  background-color: var(--placeholder-ai-hover-background);
  color: var(--placeholder-ai-hover-color);
}

/* Latest proposals: Blue palette */
.proposal-badge-source-latest {
  color: var(--placeholder-latest-color);
  background-color: var(--placeholder-latest-background);
  border-color: var(--placeholder-latest-color);
}

.proposal-badge-source-latest:hover {
  background-color: var(--placeholder-latest-hover-background);
  color: var(--placeholder-latest-hover-color);
}

/* Other proposals: Gray palette */
.proposal-badge-source-other {
  color: var(--placeholder-other-color);
  background-color: var(--placeholder-other-background);
  border-color: var(--placeholder-other-color);
}

.proposal-badge-source-other:hover {
  background-color: var(--placeholder-other-hover-background);
  color: var(--placeholder-other-hover-color);
}
</style>
